import { useEffect, useState } from 'react'
import { Trans, useTranslation } from 'react-i18next'
import { Box, IconButton, Radio, Stack } from '@mui/material'
import { PageTitle } from '../components/atoms/PageTitle'
import { Text } from '../components/atoms/Text'
import { PageSubtitle } from '../components/atoms/PageSubtitle'
import { Button } from '../components/atoms/Button'
import { callAxios } from '../hooks/useAxios'
import { useApp } from '../hooks/useApp'
import { LanguageSelector } from '../components/LanguageSelector'
import { Announcement, ID, Language, User } from '../types/models'
import { Dropdown } from '../components/atoms/Dropdown'
import { DateTime } from 'luxon'
import { Safety } from '../classes/Safety'
import { Link } from 'react-router-dom'
import { Delete } from '@mui/icons-material'

export function AdminDevOpsPage(): React.ReactElement {

  const { t } = useTranslation()
  const [alert, setAlert] = useState('')
  const [announcement, setAnnouncement] = useState('')
  const [announcements, setAnnouncements] = useState<Announcement[]>([])
  const [upgrade, setUpgrade] = useState(false)
  const [sent, setSent] = useState(false)
  const [language, setLanguage] = useState<Language>('fi')
  const [expires, setExpires] = useState(0)
  const [url, setUrl] = useState('')
  const app = useApp()

  const onSend = () => {
    setSent(true)
    callAxios({
      method: 'PUT',
      url: '/devops/alert',
    }, { message: upgrade ? 'Alert-SYSTEM_UPGRADE' : alert }).then(res => res.success && app.addInfo(t('Messsage sent.')))
  }

  const onClear = () => {
    setSent(false)
    callAxios({
      method: 'PUT',
      url: '/devops/alert',
    }, { message: '' }).then(res => {
      if (res.success) {
        app.addInfo(t('Messsage sent.'))
      } else {
        app.addError(res)
      }
    })
  }

  const onMake = () => {
    const expires_at = expires ? DateTime.now().toUTC().plus({ days: expires }) : null
    callAxios({
      method: 'POST',
      url: '/announcements',
    }, { text: announcement, language, url, expires: expires_at }).then(res => {
      if (res.success) {
        app.addInfo(t('Announcement made.'))
        onUpdate()
      } else {
        app.addError(res)
      }
    })
  }

  const onDelete = (id: ID) => {
    callAxios({
      method: 'DELETE',
      url: `/announcements/${id}/`,
    }).then(res => {
      if (res.success) {
        app.addInfo(t('Announcement deleted.'))
        onUpdate()
      } else {
        app.addError(res)
      }
    })
  }

  const onUpdate = () => {
    callAxios<Announcement[]>({
      url: '/announcements',
    }).then(res => {
      if (res.success) {
        setAnnouncements(res.data)
        const announcements = res.data.filter(a => a.language === Safety.get('language'))
        app.setUser({ ...app.user, announcements } as User)
      } else {
        app.addError(res)
      }
    })
  }

  useEffect(() => {
    onUpdate()
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  return (
    <Box>
      <PageTitle title={t('Maintenance')}/>

      <PageSubtitle title={t('Send a message to all users')}/>
      <Trans>Fill in a free form text or select one of the predefined messages.</Trans><> </>
      <Trans>Predefined messages are automatically translated to each user's selected language.</Trans>
      <Box sx={{ my: 2 }}>
        <Text value={alert} onChange={e => {
          setAlert(e.target.value)
          setUpgrade(false)
          setSent(false)
        }} fullWidth
        />
        <Radio checked={upgrade} onClick={() => {
          setAlert(t('Alert-SYSTEM_UPGRADE'))
          setUpgrade(true)
          setSent(false)
        }
        }/>
        <Trans>System Uprade</Trans>
      </Box>
      <Button onClick={onSend} disabled={sent || !alert}><Trans>Send</Trans></Button>
      <Button sx={{ ml: 4 }} onClick={onClear}><Trans>Clear</Trans></Button>

      <PageSubtitle sx={{ mt: 4 }} title={t('Add Login Announcement')}/>
      <Box sx={{ my: 2 }}>
        <Text label={t('Text')} value={announcement} required onChange={e => {
          setAnnouncement(e.target.value)
        }} fullWidth
        />
        <Stack direction="row" sx={{ mt: 2 }}>
          <Box sx={{ pt: 2 }}><Trans>Language</Trans>: {language.toUpperCase()}</Box>
          <LanguageSelector onSelect={(lang) => setLanguage(lang as Language)}/>
          <Box sx={{ pl: 4, pr: 2, pt: 2 }}><Trans>Expires In</Trans>:</Box>
          <Dropdown
            selected={`${expires}`}
            onSelect={(n) => setExpires(parseInt(n))}
            items={{
              0: t('Delete Manually'),
              1: t('1 Day'),
              2: t('2 Days'),
              3: t('3 Days'),
              4: t('4 Days'),
              5: t('5 Days'),
              6: t('6 Days'),
              7: t('7 Days'),
            }}
          />
        </Stack>
        <Text label={t('URL for Details')} value={url} onChange={e => {
          setUrl(e.target.value)
        }} fullWidth
        />

      </Box>
      <Button onClick={onMake} disabled={!announcement}><Trans>Make Announcement</Trans></Button>

      <PageSubtitle sx={{ mt: 4 }} title={t('Current Announcements')}/>
      <Box sx={{ my: 2 }}>
        {announcements.map((a, idx) =>
          <Box
            key={idx}
            borderRadius={2}
            padding={1}
            sx={{ border: '1px solid rgba(255,255,255,0.2)' }}
          >
            <Box sx={{ display: 'grid', gridTemplateColumns: '9fr 1fr' }}>
              <Box>
                {a.language.toUpperCase()}: {a.text}<br/>
                {a.expires ? a.expires : t('Delete Manually')}
              </Box>
              <IconButton onClick={() => onDelete(a.id as ID)}><Delete sx={{ color: 'error.main' }}/></IconButton>
            </Box>
            <Box>{a.url && <Link target="new" to={a.url}>{a.url}</Link>}</Box>
          </Box>)
        }
      </Box>

    </Box>
  )
}
